<template>
    <section class="signin-progress" aria-labelledby="signin-progress-title">
        <div class="member-summary">
            <img class="member-summary-background" :src="images.hero" width="995" height="150" alt="" aria-hidden="true" />
            <div class="member-profile">
                <img class="member-avatar" :src="avatarUrl" width="68" height="68" alt="客户头像" />
                <div>
                    <p class="member-greeting">{{ memberName }}</p>
                    <h1 id="signin-progress-title" class="signin-title">签到有礼</h1>
                    <p class="reward-prompt">{{ rewardPrompt }}</p>
                </div>
            </div>
            <div class="series-summary" aria-label="连续签到天数">
                <span class="series-value">{{ signDaysSeries }}</span>
                <span class="series-unit">天</span>
                <span class="series-label">连续签到</span>
            </div>
        </div>

        <div v-if="signState === 0" class="closed-notice" role="status">
            <i class="el-icon-warning-outline" aria-hidden="true"></i>
            <div>
                <strong>签到活动暂未开启</strong>
                <p>活动开启后即可参与每日签到，请稍后再来。</p>
            </div>
        </div>

        <div class="day-section">
            <div class="section-heading">
                <div>
                    <h2>连续签到领好礼</h2>
                    <p>完成一个周期或签到中断后，将从第 1 天重新计算。</p>
                </div>
                <span class="cycle-label">{{ cycle }} 天 / 周期</span>
            </div>

            <div v-if="dayItems.length" class="day-scroll" role="list" aria-label="签到周期">
                <div v-for="item in dayItems" :key="item.day" class="day-card" :class="`is-${item.state}`" role="listitem">
                    <div class="day-card-head">
                        <span>第 {{ item.day }} 天</span>
                    </div>
                    <img class="day-image" :src="dayImage(item)" width="44" height="44" alt="" aria-hidden="true" />
                    <strong class="day-state">{{ dayStateText(item) }}</strong>
                    <span v-if="item.point" class="reward-item">+{{ item.point }} 积分</span>
                    <span v-if="item.growth" class="reward-item">+{{ item.growth }} 成长值</span>
                    <span v-if="!item.point && !item.growth" class="reward-item">签到奖励</span>
                </div>
            </div>
            <div v-else class="empty-cycle" role="status">暂无可展示的签到周期，请稍后重试。</div>
        </div>

        <div class="signin-action">
            <el-button class="signin-button" type="primary" :disabled="buttonDisabled" :loading="signing" @click="$emit('sign-in')">{{ buttonText }}</el-button>
            <p class="action-tip" aria-live="polite">{{ actionTip }}</p>
        </div>

        <div class="account-summary" aria-label="签到累计奖励">
            <div class="summary-item is-point">
                <img class="summary-background" :src="images.pointCard" width="460" height="112" loading="lazy" alt="" aria-hidden="true" />
                <i class="el-icon-coin summary-icon" aria-hidden="true"></i>
                <div class="summary-content">
                    <span>签到累计积分</span>
                    <strong>{{ signPoint }}</strong>
                </div>
            </div>
            <div class="summary-item is-growth">
                <img class="summary-background" :src="images.growthCard" width="460" height="112" loading="lazy" alt="" aria-hidden="true" />
                <i class="el-icon-medal summary-icon" aria-hidden="true"></i>
                <div class="summary-content">
                    <span>签到累计成长值</span>
                    <strong>{{ signGrowth }}</strong>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
export default {
    name: "SigninProgress",
    props: {
        avatarUrl: {
            type: String,
            required: true
        },
        memberName: {
            type: String,
            required: true
        },
        signDaysSeries: {
            type: Number,
            default: 0
        },
        hasSign: {
            type: Boolean,
            default: false
        },
        signState: {
            type: Number,
            default: 0
        },
        signing: {
            type: Boolean,
            default: false
        },
        cycle: {
            type: Number,
            default: 0
        },
        dayItems: {
            type: Array,
            default: () => []
        },
        rewardPrompt: {
            type: String,
            required: true
        },
        signPoint: {
            type: Number,
            default: 0
        },
        signGrowth: {
            type: Number,
            default: 0
        },
        images: {
            type: Object,
            required: true
        }
    },
    computed: {
        buttonDisabled() {
            return this.hasSign || this.signState === 0 || this.signing
        },
        buttonText() {
            if (this.hasSign) return "今日已签到"
            if (this.signState === 0) return "签到未开启"
            return "立即签到"
        },
        actionTip() {
            if (this.hasSign) return "今日奖励已发放，明日继续签到可保持连续天数。"
            if (this.signState === 0) return "当前无法签到。"
            return "每日仅可签到一次，奖励将在签到成功后自动到账。"
        }
    },
    methods: {
        dayImage(item) {
            if (item.state === "signed") return this.images.signed
            if (item.state === "today") return this.images.today
            if (item.isBonus) return this.images.award
            return this.images.pending
        },
        dayStateText(item) {
            const stateText = {
                signed: "已完成",
                today: "今日完成",
                next: "今日待签",
                pending: item.isBonus ? "额外奖励" : "待签到"
            }
            return stateText[item.state]
        }
    }
}
</script>

<style lang="scss" scoped>
.signin-progress {
    overflow: hidden;
    border: 1px solid rgba(50, 188, 111, 0.18);
    border-radius: 8px;
    background: #ffffff;
}

.member-summary {
    box-sizing: border-box;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 150px;
    padding: 24px 32px;
    overflow: hidden;
    background: $base-color;
}

.member-summary-background {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
}

.member-profile {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
}

.member-avatar {
    width: 68px;
    height: 68px;
    margin-right: 16px;
    border: 3px solid #ffffff;
    border-radius: 50%;
    object-fit: cover;
    box-shadow: 0 4px 12px rgba(50, 188, 111, 0.14);
}

.member-greeting,
.reward-prompt,
.section-heading p,
.closed-notice p,
.action-tip {
    margin: 0;
    color: #606266;
}

.member-greeting {
    color: rgba(255, 255, 255, 0.9);
    font-size: 13px;
}

.signin-title {
    margin: 2px 0;
    color: #ffffff;
    font-size: 24px;
    line-height: 1.35;
}

.reward-prompt {
    color: #ffffff;
    font-size: 14px;
}

.series-summary {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: auto auto;
    align-items: baseline;
    min-width: 128px;
    color: #fff45c;
    text-align: right;
}

.series-value {
    font-size: 42px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    line-height: 1;
}

.series-unit {
    margin-left: 4px;
    font-size: 16px;
    font-weight: 600;
}

.series-label {
    grid-column: 1 / 3;
    margin-top: 6px;
    color: #ffffff;
    font-size: 13px;
}

.closed-notice {
    display: flex;
    align-items: center;
    margin: 24px 32px 0;
    padding: 14px 16px;
    border: 1px solid #f5dab1;
    border-radius: 6px;
    background: #fdf6ec;
    color: #b26a00;
}

.closed-notice > i {
    margin-right: 12px;
    font-size: 24px;
}

.closed-notice strong {
    color: #8a5200;
}

.closed-notice p {
    margin-top: 2px;
    color: #8a6d3b;
    font-size: 13px;
}

.day-section {
    padding: 28px 32px 8px;
}

.section-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 20px;
}

.section-heading h2 {
    margin: 0 0 4px;
    color: #303133;
    font-size: 18px;
    line-height: 1.4;
}

.section-heading p {
    font-size: 13px;
}

.cycle-label {
    flex: none;
    margin-left: 16px;
    padding: 4px 10px;
    border-radius: 12px;
    background: #f5f7fa;
    color: #606266;
    font-size: 12px;
}

.day-scroll {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding: 2px 2px 12px;
    scrollbar-width: thin;
}

.empty-cycle {
    padding: 28px;
    border: 1px dashed #dcdfe6;
    border-radius: 6px;
    color: #606266;
    text-align: center;
}

.day-card {
    box-sizing: border-box;
    flex: 1 0 104px;
    min-height: 128px;
    padding: 14px 12px;
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    background: #ffffff;
    color: #606266;
}

.day-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
}

.day-image {
    display: block;
    width: 44px;
    height: 44px;
    margin: 8px auto 0;
    object-fit: contain;
}

.day-state {
    display: block;
    margin: 5px 0 8px;
    color: #303133;
    font-size: 14px;
}

.reward-item {
    display: block;
    color: #606266;
    font-size: 12px;
    line-height: 1.6;
}

.day-card.is-signed {
    border-color: #b3e19d;
    background: #f0f9eb;
}

.day-card.is-signed .day-card-head,
.day-card.is-signed .day-state {
    color: #3f7d28;
}

.day-card.is-today {
    border-color: $base-color;
    background: rgba(50, 188, 111, 0.08);
    box-shadow: 0 0 0 2px rgba(50, 188, 111, 0.08);
}

.day-card.is-today .day-card-head,
.day-card.is-today .day-state,
.day-card.is-next .day-card-head,
.day-card.is-next .day-state {
    color: #32BC6F;
}

.day-card.is-next {
    border-style: dashed;
    border-color: $base-color;
}

.signin-action {
    padding: 20px 32px 28px;
    text-align: center;
}

.signin-button {
    min-width: 220px;
    min-height: 44px;
    border-color: #32BC6F;
    background: #32BC6F;
    font-size: 15px;
}

.signin-button:not(.is-disabled):hover,
.signin-button:not(.is-disabled):focus {
    border-color: #32BC6F;
    background: #32BC6F;
}

.action-tip {
    margin-top: 10px;
    font-size: 12px;
}

.account-summary {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    padding: 20px 32px 28px;
    border-top: 1px solid #ebeef5;
    background: #fafafa;
}

.summary-item {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 112px;
    padding: 0 24px;
    overflow: hidden;
    border-radius: 8px;
    background: #32BC6F;
}

.summary-item.is-growth {
    background: #4f91e8;
}

.summary-background {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.summary-icon {
    position: relative;
    z-index: 1;
    margin-right: 14px;
    color: #ffffff;
    font-size: 28px;
}

.summary-content {
    position: relative;
    z-index: 1;
}

.summary-item span,
.summary-item strong {
    display: block;
}

.summary-item span {
    color: rgba(255, 255, 255, 0.9);
    font-size: 13px;
}

.summary-item strong {
    margin-top: 2px;
    color: #ffffff;
    font-size: 22px;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
}

@media (max-width: 900px) {
    .member-summary,
    .section-heading {
        align-items: flex-start;
    }

    .account-summary {
        grid-template-columns: 1fr;
    }
}

@media (prefers-reduced-motion: reduce) {
    .signin-button,
    .day-card {
        transition: none !important;
    }
}
</style>
